import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiArchive,
  FiCheckCircle,
  FiClock,
  FiDatabase,
  FiDownloadCloud,
  FiFileText,
  FiHardDrive,
  FiLock,
  FiRefreshCw,
  FiShield,
  FiUploadCloud,
} from 'react-icons/fi';
import { api } from '../services/api';
import './AdminBackups.css';

const formatBytes = (value) => {
  const bytes = Number(value) || 0;
  if (!bytes) return '۰ بایت';
  const units = ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const amount = bytes / (1024 ** unitIndex);
  return `${amount.toLocaleString('fa-IR', { maximumFractionDigits: unitIndex ? 1 : 0 })} ${units[unitIndex]}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' });
};

const operationLabels = {
  export: 'خروجی',
  restore: 'بازیابی',
};

const modeLabels = {
  full: 'کامل',
  data: 'فقط داده‌ها',
  custom: 'انتخابی',
  merge: 'ادغام',
  replace: 'جایگزینی کامل',
};

const statusLabels = {
  processing: 'در حال انجام',
  succeeded: 'موفق',
  failed: 'ناموفق',
};

const AdminBackups = () => {
  const fileInputRef = useRef(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [notice, setNotice] = useState(null);
  const [exportScope, setExportScope] = useState('full');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [exportPassword, setExportPassword] = useState('');
  const [exporting, setExporting] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [importPassword, setImportPassword] = useState('');
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restoreFiles, setRestoreFiles] = useState(true);
  const [replaceConfirmation, setReplaceConfirmation] = useState('');
  const [restoring, setRestoring] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await api.get('/api/backups/overview');
      setOverview(response.data);
      setSelectedGroups((current) => (
        current.length ? current : (response.data?.groups || []).map((group) => group.key)
      ));
    } catch (error) {
      setPageError(error.data?.message || error.message || 'اطلاعات پشتیبان‌گیری دریافت نشد');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const collectionCountMap = useMemo(() => new Map(
    (overview?.collections || []).map((collection) => [collection.key, collection.count])
  ), [overview]);

  const lastSuccessfulExport = useMemo(() => (
    overview?.history?.find((item) => item.operation === 'export' && item.status === 'succeeded')
  ), [overview]);

  const lastSuccessfulRestore = useMemo(() => (
    overview?.history?.find((item) => item.operation === 'restore' && item.status === 'succeeded')
  ), [overview]);

  const handleScopeChange = (scope) => {
    setExportScope(scope);
    if (scope === 'full') setIncludeFiles(true);
    if (scope === 'data') setIncludeFiles(false);
    setNotice(null);
  };

  const toggleGroup = (groupKey) => {
    setSelectedGroups((current) => (
      current.includes(groupKey)
        ? current.filter((key) => key !== groupKey)
        : [...current, groupKey]
    ));
  };

  const saveBlob = ({ blob, filename }) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleExport = async () => {
    if (!exportPassword) {
      setNotice({ tone: 'error', text: 'رمز عبور فعلی مدیر را وارد کنید.' });
      return;
    }
    if (exportScope === 'custom' && !selectedGroups.length && !includeFiles) {
      setNotice({ tone: 'error', text: 'حداقل یک گروه داده یا فایل‌ها را انتخاب کنید.' });
      return;
    }

    setExporting(true);
    setNotice(null);
    try {
      const result = await api.downloadPost('/api/backups/export', {
        scope: exportScope,
        selectedGroups,
        includeFiles,
        password: exportPassword,
      });
      saveBlob(result);
      setExportPassword('');
      setNotice({
        tone: 'success',
        text: 'نسخهٔ پشتیبان ساخته و دانلود شد. آن را در یک محل امن و جدا از هاست نگه دارید.',
      });
      await loadOverview();
    } catch (error) {
      setNotice({ tone: 'error', text: error.data?.message || error.message || 'ساخت نسخهٔ پشتیبان انجام نشد' });
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBackupFile(file);
    setInspection(null);
    setReplaceConfirmation('');
    setNotice(null);
  };

  const handleInspect = async () => {
    if (!backupFile) {
      setNotice({ tone: 'error', text: 'ابتدا فایل ZIP پشتیبان را انتخاب کنید.' });
      return;
    }
    if (!importPassword) {
      setNotice({ tone: 'error', text: 'رمز عبور فعلی مدیر را وارد کنید.' });
      return;
    }

    setInspecting(true);
    setInspection(null);
    setNotice(null);
    try {
      const formData = new FormData();
      formData.append('backup', backupFile);
      formData.append('password', importPassword);
      const response = await api.upload('/api/backups/inspect', formData);
      setInspection(response.data);
      setRestoreFiles(Boolean(response.data?.fileCount));
      setNotice({ tone: 'success', text: response.message });
    } catch (error) {
      setNotice({ tone: 'error', text: error.data?.message || error.message || 'فایل پشتیبان تأیید نشد' });
    } finally {
      setInspecting(false);
    }
  };

  const resetImport = () => {
    setBackupFile(null);
    setInspection(null);
    setRestoreMode('merge');
    setRestoreFiles(true);
    setReplaceConfirmation('');
    setImportPassword('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRestore = async () => {
    if (!inspection?.restoreToken) return;
    if (!importPassword) {
      setNotice({ tone: 'error', text: 'رمز عبور فعلی مدیر را وارد کنید.' });
      return;
    }
    if (restoreMode === 'replace' && replaceConfirmation !== 'بازیابی کامل') {
      setNotice({ tone: 'error', text: 'عبارت «بازیابی کامل» را دقیق وارد کنید.' });
      return;
    }

    const confirmationText = restoreMode === 'replace'
      ? 'تمام مجموعه‌های موجود در این فایل جایگزین داده‌های فعلی می‌شوند. ادامه می‌دهید؟'
      : 'داده‌های این نسخه با داده‌های فعلی ادغام می‌شوند. ادامه می‌دهید؟';
    if (!window.confirm(confirmationText)) return;

    setRestoring(true);
    setNotice(null);
    try {
      const response = await api.post('/api/backups/restore', {
        restoreToken: inspection.restoreToken,
        mode: restoreMode,
        restoreFiles,
        password: importPassword,
        confirmation: restoreMode === 'replace' ? replaceConfirmation : '',
      });
      setNotice({
        tone: 'success',
        text: `${response.message}. ${Number(response.data?.documentCount || 0).toLocaleString('fa-IR')} رکورد پردازش شد.`,
      });
      resetImport();
      await loadOverview();
    } catch (error) {
      setNotice({ tone: 'error', text: error.data?.message || error.message || 'بازیابی نسخهٔ پشتیبان انجام نشد' });
      setInspection(null);
    } finally {
      setRestoring(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="admin-backup-loading">
        <FiRefreshCw className="is-spinning" aria-hidden="true" />
        <span>در حال بررسی داده‌ها و فایل‌های سایت...</span>
      </div>
    );
  }

  return (
    <div className="admin-backups" dir="rtl">
      <section className="admin-backup-hero">
        <div>
          <span className="admin-backup-eyebrow"><FiShield /> مرکز حفاظت از داده‌ها</span>
          <h2>نسخهٔ قابل بازیابی از تمام اطلاعات هریاد</h2>
          <p>
            داده‌های MongoDB، تصاویر، فایل محصولات و پیوست سفارش‌ها را خروجی بگیرید
            یا یک نسخهٔ قبلی را پس از بررسی ایمنی بازیابی کنید.
          </p>
        </div>
        <button type="button" className="admin-backup-refresh" onClick={loadOverview} disabled={loading}>
          <FiRefreshCw className={loading ? 'is-spinning' : ''} />
          به‌روزرسانی آمار
        </button>
      </section>

      {pageError && <div className="admin-backup-notice is-error"><FiAlertTriangle /> {pageError}</div>}
      {notice && (
        <div className={`admin-backup-notice is-${notice.tone}`}>
          {notice.tone === 'success' ? <FiCheckCircle /> : <FiAlertTriangle />}
          {notice.text}
        </div>
      )}

      <section className="admin-backup-stats" aria-label="خلاصه اطلاعات قابل پشتیبان‌گیری">
        <article>
          <span className="is-blue"><FiDatabase /></span>
          <div><small>رکوردهای پایگاه داده</small><strong>{Number(overview?.totals?.documents || 0).toLocaleString('fa-IR')}</strong></div>
        </article>
        <article>
          <span className="is-cyan"><FiFileText /></span>
          <div><small>فایل‌های ذخیره‌شده</small><strong>{Number(overview?.totals?.files || 0).toLocaleString('fa-IR')}</strong></div>
        </article>
        <article>
          <span className="is-violet"><FiHardDrive /></span>
          <div><small>حجم فایل‌ها</small><strong>{formatBytes(overview?.totals?.fileSize)}</strong></div>
        </article>
        <article>
          <span className="is-green"><FiClock /></span>
          <div><small>آخرین بک‌آپ موفق</small><strong>{formatDate(lastSuccessfulExport?.completedAt)}</strong></div>
        </article>
      </section>

      <div className="admin-backup-grid">
        <section className="admin-backup-panel">
          <header>
            <span className="admin-backup-panel-icon is-export"><FiDownloadCloud /></span>
            <div>
              <h3>ساخت و دانلود نسخهٔ پشتیبان</h3>
              <p>نوع خروجی را انتخاب کنید؛ فایل ساخته‌شده مستقیماً روی دستگاه شما دانلود می‌شود.</p>
            </div>
          </header>

          <div className="admin-backup-scope-cards">
            {[
              { key: 'full', title: 'بک‌آپ کامل', text: 'تمام داده‌ها و همهٔ فایل‌ها', icon: FiArchive },
              { key: 'data', title: 'فقط داده‌ها', text: 'همهٔ رکوردها بدون فایل‌های آپلودی', icon: FiDatabase },
              { key: 'custom', title: 'خروجی انتخابی', text: 'انتخاب گروه‌های دلخواه', icon: FiFileText },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={exportScope === item.key ? 'is-active' : ''}
                  onClick={() => handleScopeChange(item.key)}
                  aria-pressed={exportScope === item.key}
                >
                  <span><Icon /></span>
                  <strong>{item.title}</strong>
                  <small>{item.text}</small>
                </button>
              );
            })}
          </div>

          {exportScope === 'custom' && (
            <div className="admin-backup-groups">
              <h4>گروه‌های داده</h4>
              {(overview?.groups || []).map((group) => {
                const groupCount = (overview?.collections || [])
                  .filter((collection) => collection.group === group.key)
                  .reduce((sum, collection) => sum + collection.count, 0);
                return (
                  <label key={group.key} className={selectedGroups.includes(group.key) ? 'is-selected' : ''}>
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.key)}
                      onChange={() => toggleGroup(group.key)}
                    />
                    <span>
                      <strong>{group.label}</strong>
                      <small>{group.description}</small>
                    </span>
                    <b>{groupCount.toLocaleString('fa-IR')}</b>
                  </label>
                );
              })}
              <label className={`admin-backup-files-option ${includeFiles ? 'is-selected' : ''}`}>
                <input type="checkbox" checked={includeFiles} onChange={(event) => setIncludeFiles(event.target.checked)} />
                <span>
                  <strong>فایل‌های آپلودی</strong>
                  <small>تصاویر، ویدیوها، محصولات دانلودی و پیوست سفارش‌ها</small>
                </span>
                <b>{formatBytes(overview?.totals?.fileSize)}</b>
              </label>
            </div>
          )}

          <label className="admin-backup-password">
            <span><FiLock /> تأیید هویت مدیر</span>
            <input
              type="password"
              value={exportPassword}
              onChange={(event) => setExportPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="رمز عبور فعلی خود را وارد کنید"
            />
          </label>

          <button
            type="button"
            className="admin-backup-primary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <FiRefreshCw className="is-spinning" /> : <FiDownloadCloud />}
            {exporting ? 'در حال ساخت فایل پشتیبان...' : 'ساخت و دانلود فایل ZIP'}
          </button>
        </section>

        <section className="admin-backup-panel">
          <header>
            <span className="admin-backup-panel-icon is-import"><FiUploadCloud /></span>
            <div>
              <h3>بررسی و بازیابی نسخهٔ پشتیبان</h3>
              <p>فایل تا پایان بررسی هیچ تغییری در سایت ایجاد نمی‌کند.</p>
            </div>
          </header>

          <label className={`admin-backup-dropzone ${backupFile ? 'has-file' : ''}`}>
            <input ref={fileInputRef} type="file" accept=".zip,application/zip" onChange={handleFileChange} />
            <span><FiUploadCloud /></span>
            <strong>{backupFile ? backupFile.name : 'فایل ZIP پشتیبان را انتخاب کنید'}</strong>
            <small>{backupFile ? formatBytes(backupFile.size) : 'فقط فایل ساخته‌شده توسط سیستم پشتیبان هریاد'}</small>
          </label>

          <label className="admin-backup-password">
            <span><FiLock /> تأیید هویت مدیر</span>
            <input
              type="password"
              value={importPassword}
              onChange={(event) => setImportPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="رمز عبور فعلی خود را وارد کنید"
            />
          </label>

          {!inspection && (
            <button
              type="button"
              className="admin-backup-secondary"
              onClick={handleInspect}
              disabled={inspecting || !backupFile}
            >
              {inspecting ? <FiRefreshCw className="is-spinning" /> : <FiShield />}
              {inspecting ? 'در حال بررسی کامل فایل...' : 'بررسی سلامت و سازگاری'}
            </button>
          )}

          {inspection && (
            <div className="admin-backup-inspection">
              <div className="admin-backup-inspection-title">
                <span><FiCheckCircle /></span>
                <div>
                  <strong>فایل سالم و سازگار است</strong>
                  <small>ساخته‌شده در {formatDate(inspection.manifest?.generatedAt)}</small>
                </div>
              </div>

              <div className="admin-backup-inspection-stats">
                <span><small>رکوردها</small><strong>{Number(inspection.documentCount || 0).toLocaleString('fa-IR')}</strong></span>
                <span><small>فایل‌ها</small><strong>{Number(inspection.fileCount || 0).toLocaleString('fa-IR')}</strong></span>
                <span><small>حجم ZIP</small><strong>{formatBytes(inspection.archiveSize)}</strong></span>
              </div>

              <div className="admin-backup-comparison">
                <h4>مقایسه با داده‌های فعلی</h4>
                {(inspection.manifest?.collections || []).map((collection) => {
                  const current = Number(collectionCountMap.get(collection.key) || 0);
                  const backup = Number(collection.count || 0);
                  const delta = backup - current;
                  return (
                    <div key={collection.key}>
                      <span>{collection.label}</span>
                      <small>فعلی: {current.toLocaleString('fa-IR')}</small>
                      <strong>{backup.toLocaleString('fa-IR')}</strong>
                      <b className={delta > 0 ? 'is-positive' : delta < 0 ? 'is-negative' : ''}>
                        {delta === 0 ? 'برابر' : `${delta > 0 ? '+' : ''}${delta.toLocaleString('fa-IR')}`}
                      </b>
                    </div>
                  );
                })}
              </div>

              <div className="admin-backup-restore-modes">
                <label className={restoreMode === 'merge' ? 'is-selected' : ''}>
                  <input type="radio" name="restoreMode" value="merge" checked={restoreMode === 'merge'} onChange={() => setRestoreMode('merge')} />
                  <span>
                    <strong>ادغام ایمن</strong>
                    <small>رکوردهای هم‌شناسه به‌روزرسانی و رکوردهای جدید اضافه می‌شوند.</small>
                  </span>
                </label>
                <label className={restoreMode === 'replace' ? 'is-selected is-danger' : ''}>
                  <input type="radio" name="restoreMode" value="replace" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')} />
                  <span>
                    <strong>جایگزینی کامل</strong>
                    <small>مجموعه‌های موجود در فایل پاک و دقیقاً با نسخهٔ پشتیبان جایگزین می‌شوند.</small>
                  </span>
                </label>
              </div>

              {inspection.fileCount > 0 && (
                <label className="admin-backup-restore-files">
                  <input type="checkbox" checked={restoreFiles} onChange={(event) => setRestoreFiles(event.target.checked)} />
                  <span>
                    <strong>فایل‌های آپلودی نیز بازیابی شوند</strong>
                    <small>{inspection.fileCount.toLocaleString('fa-IR')} فایل، مجموعاً {formatBytes(inspection.fileSize)}</small>
                  </span>
                </label>
              )}

              {restoreMode === 'replace' && (
                <div className="admin-backup-danger-confirmation">
                  <div><FiAlertTriangle /><span><strong>این عملیات مخرب است.</strong> ابتدا از وضعیت فعلی یک بک‌آپ کامل دانلود کنید.</span></div>
                  <label>
                    برای تأیید، عبارت <b>بازیابی کامل</b> را وارد کنید:
                    <input
                      type="text"
                      value={replaceConfirmation}
                      onChange={(event) => setReplaceConfirmation(event.target.value)}
                      autoComplete="off"
                      placeholder="بازیابی کامل"
                    />
                  </label>
                </div>
              )}

              <div className="admin-backup-restore-actions">
                <button type="button" className="admin-backup-danger" onClick={handleRestore} disabled={restoring}>
                  {restoring ? <FiRefreshCw className="is-spinning" /> : <FiUploadCloud />}
                  {restoring ? 'در حال بازیابی و کنترل نتیجه...' : 'شروع بازیابی'}
                </button>
                <button type="button" className="admin-backup-text-button" onClick={resetImport} disabled={restoring}>
                  انتخاب فایل دیگر
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="admin-backup-security">
        <span><FiLock /></span>
        <div>
          <h3>نکات امنیتی مهم</h3>
          <p>
            فایل بک‌آپ شامل هش رمز کاربران و تنظیمات رمزگذاری‌شدهٔ ایمیل، پیامک و درگاه است؛
            آن را عمومی نکنید. فایل <code>.env</code> داخل بک‌آپ قرار نمی‌گیرد و برای انتقال
            به سرور دیگر باید مقدار <code>SETTINGS_ENCRYPTION_KEY</code> قبلی را حفظ کنید؛
            اگر این مقدار تعریف نشده، کلید جایگزین آن یعنی <code>JWT_SECRET</code> باید ثابت بماند.
          </p>
        </div>
      </section>

      <section className="admin-backup-history">
        <header>
          <div>
            <span><FiClock /></span>
            <div><h3>تاریخچهٔ عملیات</h3><p>آخرین خروجی‌ها و بازیابی‌های انجام‌شده در پنل</p></div>
          </div>
          {lastSuccessfulRestore && <small>آخرین بازیابی موفق: {formatDate(lastSuccessfulRestore.completedAt)}</small>}
        </header>
        <div className="admin-backup-history-table">
          <table>
            <thead>
              <tr>
                <th>عملیات</th>
                <th>نوع</th>
                <th>وضعیت</th>
                <th>رکورد</th>
                <th>فایل</th>
                <th>مدیر</th>
                <th>تاریخ</th>
              </tr>
            </thead>
            <tbody>
              {!overview?.history?.length ? (
                <tr><td colSpan="7" className="admin-backup-empty">هنوز عملیاتی ثبت نشده است.</td></tr>
              ) : overview.history.map((item) => (
                <tr key={item.id}>
                  <td><strong>{operationLabels[item.operation] || item.operation}</strong></td>
                  <td>{modeLabels[item.mode] || item.mode}</td>
                  <td><span className={`admin-backup-status is-${item.status}`}>{statusLabels[item.status] || item.status}</span></td>
                  <td>{Number(item.documentCount || 0).toLocaleString('fa-IR')}</td>
                  <td>{Number(item.fileCount || 0).toLocaleString('fa-IR')}</td>
                  <td>{item.requestedBy?.name || '—'}</td>
                  <td>{formatDate(item.completedAt || item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminBackups;
